import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ApolloError, useQuery } from '@apollo/client';

import useMediaQuery from 'shared/hooks/use-media-query';
import { mediaQueries } from 'shared/styles';
import { isIOS } from 'shared/helpers/utils';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { GetLoyaltyPassUrlDocument } from 'src/gql/graphql';
import { useIsFullstackDispensary } from 'src/hooks/use-is-fullstack-dispensary';
import { getLogger } from 'src/utils/datadog-logger';

const logger = getLogger('account-loyalty-pass');

type UseLoyaltyPassReturn = {
  shouldShow: boolean;
  loyaltyPassUrl: string | null;
  loyaltyPassShortUrl: string | null;
  loading: boolean;
  // Optional so existing mocks in other test suites that predate this field don't need updating.
  error?: boolean;
};

/**
 * Simplified hook for fetching loyalty pass URL in Loyalty Component.
 *
 * This hook is a streamlined version of `useLoyaltyPassUrl` designed for use in
 * the loyalty component where the user's loyalty enrollment status is already confirmed
 * (status === Statuses.success). It skips enrollment validation and rewards data
 * fetching, focusing only on:
 * 1. Feature flag check
 * 2. Device compatibility (iOS or desktop, NOT Android mobile)
 * 3. Loyalty pass URL fetching
 *
 * Eligibility requirements:
 * - Feature flag `ecomm.loyalty-pass-ctas.rollout` must be enabled
 * - Dispensary must be a fullstack dispensary
 * - User must be on a compatible device (desktop or iOS mobile)
 *
 * Device compatibility:
 * - ✓ Desktop (all platforms)
 * - ✓ Mobile iOS (iPhone/iPad)
 * - ✗ Mobile Android/other
 *
 * @returns Loyalty pass URLs, loading state, and visibility flag
 *
 * @example
 * const { shouldShow, loyaltyPassUrl, loyaltyPassShortUrl, loading } = useLoyaltyPass();
 *
 * if (!shouldShow || loading) {
 *   return null;
 * }
 */
export const useLoyaltyPass = (): UseLoyaltyPassReturn => {
  const flags = useFlags();
  const { dispensary } = useDispensary();
  const isFullstackDispensary = useIsFullstackDispensary();

  // Device compatibility check
  const isIpadorSmaller = useMediaQuery(mediaQueries.largeTablet); // ≤960px
  const userIsIOS = useMemo(() => isIOS(), []);

  // Feature flag check
  const isShowLoyaltyPassFlagEnabled = flags['ecomm.loyalty-pass-ctas.rollout'] ?? false;

  // Determine if should skip URL query
  const skipQuery =
    !dispensary?.id || !isShowLoyaltyPassFlagEnabled || !isFullstackDispensary || (isIpadorSmaller && !userIsIOS); // Skip on Android mobile

  // Fetch loyalty pass URL
  const { data: loyaltyPassUrlData, loading: loyaltyPassUrlLoading, error: loyaltyPassUrlError } = useQuery(
    GetLoyaltyPassUrlDocument,
    {
      variables: {
        dispensaryId: dispensary?.id,
      },
      skip: skipQuery,
      onError(error: ApolloError) {
        logger.error('Failed to get loyalty pass URL', {
          dispensaryId: dispensary?.id,
          error,
        });
      },
    }
  );

  const shortUrl = loyaltyPassUrlData?.getLoyaltyPassUrl?.shortUrl;
  const url = loyaltyPassUrlData?.getLoyaltyPassUrl?.url;

  const loyaltyPassUrl = skipQuery ? null : url ?? null;
  const loyaltyPassShortUrl = skipQuery ? null : shortUrl ?? url ?? null;
  const shouldShow = !!loyaltyPassUrl && !loyaltyPassUrlError;

  return {
    shouldShow,
    loyaltyPassUrl,
    loyaltyPassShortUrl,
    loading: loyaltyPassUrlLoading,
    error: !skipQuery && !!loyaltyPassUrlError,
  };
};
